if (!customElements.get('complementary-product-slider')) {
  customElements.define(
    'complementary-product-slider',
    class ComplementaryProductSlider extends HTMLElement {
      constructor() {
        super();

        this.enableSliderDesktop = this.dataset.enableSliderDesktop === 'true';
        this.enableSliderMobile = this.dataset.enableSliderMobile === 'true';

        this.selectors = {
          productsWrap: '.products-wrap',
          products: '.products',
          pagination: '.swiper-pagination',
        };

        this.classes = {
          grid: 'f-grid',
          swiper: 'swiper',
          swiperWrapper: 'swiper-wrapper',
        };

        this.productsWrap = this.querySelector(this.selectors.productsWrap);
        this.products = this.querySelector(this.selectors.products);
        this.sliderInstance = false;

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.init.bind(this);
        this.init();
      }

      connectedCallback() {
        // Listen for recommendations loaded event (products load dynamically)
        const productRecommendations = this.closest('product-recommendations');
        if (productRecommendations) {
          productRecommendations.addEventListener('recommendations:loaded', () => {
            setTimeout(() => {
              this.setupAddAllButton();
              this.setupVariantListeners();
            }, 100);
          });
        }
        
        // Set up event delegation for add all button
        // Use a small delay to ensure DOM is ready, or use MutationObserver
        setTimeout(() => {
          this.setupAddAllButton();
          this.setupVariantListeners();
        }, 100);
        
        // Also set up observer for dynamic content
        const container = this.closest('.complementary-products__container') || this.closest('aside');
        if (container) {
          const observer = new MutationObserver(() => {
            this.setupAddAllButton();
            this.setupVariantListeners();
          });
          observer.observe(container, { childList: true, subtree: true });
          this._observer = observer;
        }
      }

      setupVariantListeners() {
        // Listen for variant changes in product cards
        const productsContainer = this.querySelector('.products') || 
                                  this.querySelector('.products-wrap') || 
                                  this.closest('.complementary-products__container') ||
                                  this;
        
        // Remove existing listeners to avoid duplicates
        if (this._variantChangeHandler) {
          productsContainer.removeEventListener('change', this._variantChangeHandler);
          productsContainer.removeEventListener('variant:change', this._variantChangeHandler);
        }
        
        // Create handler
        this._variantChangeHandler = (event) => {
          const select = event.target.closest('select[name="id"]');
          if (select && select.closest('[data-product-id]')) {
            const productEl = select.closest('[data-product-id]');
            const hiddenInput = productEl.querySelector('input.complementary-product-variant-id');
            if (hiddenInput) {
              hiddenInput.value = select.value;
              hiddenInput.disabled = select.querySelector(`option[value="${select.value}"]`)?.disabled || false;
            }
            // Update data attribute too
            if (productEl) {
              productEl.dataset.variantId = select.value;
            }
          }
        };
        
        // Add listeners
        productsContainer.addEventListener('change', this._variantChangeHandler);
        productsContainer.addEventListener('variant:change', this._variantChangeHandler);
      }

      disconnectedCallback() {
        if (this._observer) {
          this._observer.disconnect();
        }
      }

      setupAddAllButton() {
        const container = this.closest('.complementary-products__container') || this.closest('aside');
        if (container) {
          const addAllButton = container.querySelector('[data-add-all-complementary-products]');
          if (addAllButton && !addAllButton.dataset.listenerAttached) {
            addAllButton.addEventListener('click', this.handleAddAllClick.bind(this));
            addAllButton.dataset.listenerAttached = 'true';
          }
        }
      }

      collectProducts() {
        const items = [];
        // Search within the products container (could be .products or .products-wrap)
        // Also search in parent containers in case structure is different
        const productsContainer = this.querySelector('.products') || 
                                  this.querySelector('.products-wrap') || 
                                  this.closest('.complementary-products__container') ||
                                  this;
        
        // First, try to find our custom hidden inputs (most reliable)
        const variantInputs = productsContainer.querySelectorAll('input.complementary-product-variant-id');
        if (variantInputs.length > 0) {
          variantInputs.forEach((input) => {
            if (input.value && !input.disabled) {
              const variantId = parseInt(input.value, 10);
              if (!isNaN(variantId)) {
                items.push({
                  id: variantId,
                  quantity: 1
                });
              }
            }
          });
          return items;
        }
        
        // Fallback: Find all product cards - they could be in f-column swiper-slide or directly as product-card
        let productCards = productsContainer.querySelectorAll('[data-product-id], .product-card');
        
        // If no cards found with that selector, try alternatives
        if (productCards.length === 0) {
          productCards = productsContainer.querySelectorAll('.f-column .product-card, .f-column[data-product-id]');
        }
        
        productCards.forEach((card) => {
          // Try to find variant ID from data attribute first
          if (card.dataset.variantId) {
            const variantId = parseInt(card.dataset.variantId, 10);
            if (!isNaN(variantId)) {
              items.push({
                id: variantId,
                quantity: 1
              });
              return;
            }
          }
          
          // Try to find variant ID from product form
          const form = card.querySelector('form[data-type="add-to-cart-form"], form.product-form, form[is="product-form"]');
          if (form) {
            // Look for variant input - could be input[name="id"] or input.product-variant-id
            const variantInput = form.querySelector('input[name="id"], input.product-variant-id, input[type="hidden"][name="id"]');
            if (variantInput && variantInput.value && !variantInput.disabled) {
              const variantId = parseInt(variantInput.value, 10);
              if (!isNaN(variantId)) {
                const quantityInput = form.querySelector('input[name="quantity"], quantity-input input, quantity-input');
                let quantity = 1;
                if (quantityInput) {
                  const qtyValue = quantityInput.value || quantityInput.textContent;
                  quantity = parseInt(qtyValue, 10) || 1;
                }
                
                items.push({
                  id: variantId,
                  quantity: quantity
                });
              }
            }
          } else {
            // Fallback: try to find variant input directly in the card
            const variantInput = card.querySelector('input[name="id"], input.product-variant-id, input[type="hidden"][name="id"]');
            if (variantInput && variantInput.value && !variantInput.disabled) {
              const variantId = parseInt(variantInput.value, 10);
              if (!isNaN(variantId)) {
                items.push({
                  id: variantId,
                  quantity: 1
                });
              }
            }
          }
        });
        
        return items;
      }

      async handleAddAllClick(event) {
        event.preventDefault();
        const button = event.target.closest('[data-add-all-complementary-products]');
        if (!button) return;

        // Wait a bit to ensure products are fully rendered
        await new Promise(resolve => setTimeout(resolve, 50));
        
        let items = this.collectProducts();
        
        if (items.length === 0) {
          // Try once more after a short delay in case products are still loading
          await new Promise(resolve => setTimeout(resolve, 200));
          items = this.collectProducts();
          
          if (items.length === 0) {
            console.warn('No products found. Debug info:', {
              productsContainer: this.querySelector('.products') ? 'found' : 'not found',
              productCards: this.querySelectorAll('.product-card').length,
              forms: this.querySelectorAll('form[data-type="add-to-cart-form"]').length,
              variantInputs: this.querySelectorAll('input[name="id"]').length
            });
            alert('No available products found. Please ensure products are in stock and variants are selected.');
            return;
          }
        }

        button.setAttribute('aria-disabled', 'true');
        button.classList.add('btn--loading');

        const config = FoxTheme.utils.fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        
        // Get sections to update
        let sectionsToUpdate = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', {
            bubbles: true,
            detail: { sections: sectionsToUpdate }
          })
        );

        config.body = JSON.stringify({
          sections: sectionsToUpdate,
          sections_url: window.location.pathname,
          items: items.map(item => ({
            id: item.id,
            quantity: item.quantity
          }))
        });

        try {
          const response = await fetch(`${FoxTheme.routes.cart_add_url}`, config);
          const parsedState = await response.json();

          if (parsedState.status || parsedState.errors) {
            const errorMessage = parsedState.description || parsedState.message || 'Unable to add products to cart.';
            alert(errorMessage);
            
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, {
              source: 'complementary-products',
              errors: parsedState.errors || parsedState.description,
              message: parsedState.message
            });

            document.dispatchEvent(
              new CustomEvent('product-ajax:error', {
                detail: {
                  errorMessage: errorMessage
                }
              })
            );
            return;
          }

          const cartJson = await (
            await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })
          ).json();
          cartJson['sections'] = parsedState['sections'];

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
          
          document.dispatchEvent(
            new CustomEvent('product-ajax:added', {
              detail: {
                product: parsedState
              }
            })
          );

          // Show cart drawer if available
          const cartDrawerEl = document.querySelector('cart-drawer');
          if (cartDrawerEl) {
            cartDrawerEl.show(button);
          }
        } catch (error) {
          console.error('Error adding products to cart:', error);
          alert('An error occurred while adding products to cart. Please try again.');
        } finally {
          button.classList.remove('btn--loading');
          button.removeAttribute('aria-disabled');
        }
      }

      init() {
        if (FoxTheme.config.mqlMobile) {
          this.enableSliderMobile ? this.initSlider() : this.destroySlider();
        } else {
          this.enableSliderDesktop ? this.initSlider() : this.destroySlider();
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          const columnsDesktop = this.dataset.columnsDesktop;
          const columnsMobile = this.dataset.columnsMobile;
          const columnGap = window.getComputedStyle(this.products).getPropertyValue('--f-column-gap');
          const spaceBetween = columnGap !== '' ? parseFloat(columnGap.replace('rem', '')) * 10 : 10;

          this.sliderOptions = {
            slidesPerView: 1.2,
            breakpoints: {
              768: {
                slidesPerView: 2,
              },
            },
            spaceBetween: 20,
            loop: false,
            grabCursor: false,
            allowTouchMove: true,
            autoHeight: true,
            navigation: false,
          };

          const paginationEl = this.querySelector(this.selectors.pagination);
          if (paginationEl) {
            this.sliderOptions = {
              ...this.sliderOptions,
              pagination: {
                el: paginationEl,
                clickable: true,
                type: 'progressbar',
              },
            };
          }

          this.productsWrap.classList.add(this.classes.swiper);
          this.products.classList.remove(this.classes.grid);
          this.products.classList.add(this.classes.swiperWrapper);
          this.sliderInstance = new window.FoxTheme.Carousel(this.productsWrap, this.sliderOptions);
          this.sliderInstance.init();

          this.fixQuickviewDuplicate();
          this.calcNavButtonsPosition();
        }
      }

      fixQuickviewDuplicate() {
        let modalIds = [];
        Array.from(this.querySelectorAll('quick-view-modal')).forEach((modal) => {
          const modalID = modal.getAttribute('id');
          if (modalIds.includes(modalID)) {
            modal.remove();
          } else {
            modalIds.push(modalID);
          }
        });
      }

      calcNavButtonsPosition() {
        if (!this.dataset.calcButtonPosition === 'true') return;

        const firstMedia = this.querySelector('.product-card__image-wrapper');
        if (firstMedia && firstMedia.clientHeight > 0) {
          this.style.setProperty('--swiper-navigation-top-offset', parseInt(firstMedia.clientHeight) / 2 + 'px');
        }
      }

      destroySlider() {
        this.productsWrap.classList.remove(this.classes.swiper);
        this.products.classList.remove(this.classes.swiperWrapper);
        this.products.classList.add(this.classes.grid);
        if (typeof this.sliderInstance !== 'object') return;
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }
    }
  );
}
